import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import logging from "../utils/logging";
import Header from "../components/Header";
import AlertTimeline from "../components/AlertTimeline";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { CheckCircle, ShieldAlert, Flame, HeartPulse, AlertTriangle, MapPin, TableIcon, Clock, Download, FileText, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { exportCSV, exportPDF } from "../utils/exportReports";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const typeIcons = { security: ShieldAlert, fire: Flame, medical: HeartPulse, accident: AlertTriangle };
const typeColors = {
  security: "bg-red-100 text-red-800 hover:bg-red-100",
  fire: "bg-orange-100 text-orange-800 hover:bg-orange-100",
  medical: "bg-blue-100 text-blue-800 hover:bg-blue-100",
  accident: "bg-yellow-100 text-yellow-800 hover:bg-yellow-100",
};
const statusColors = {
  active: "bg-red-100 text-red-800 hover:bg-red-100",
  resolved: "bg-green-100 text-green-800 hover:bg-green-100",
};

const iconColorMap = {
  security: "#EF4444",
  fire: "#F97316",
  medical: "#3B82F6",
  accident: "#EAB308",
};

function formatDate(isoStr) {
  return new Date(isoStr).toLocaleString(undefined, {
    month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

function ReportFilters({ typeFilter, setTypeFilter, statusFilter, setStatusFilter }) {
  const { t } = useTranslation();
  return (
    <div className="flex gap-3">
      <Select value={typeFilter} onValueChange={setTypeFilter}>
        <SelectTrigger className="w-36 rounded-sm border-gray-300 font-body text-sm" data-testid="type-filter-trigger">
          <SelectValue placeholder={t("reports.all_types")} />
        </SelectTrigger>
        <SelectContent className="rounded-sm">
          <SelectItem value="all">{t("reports.all_types")}</SelectItem>
          <SelectItem value="security">{t("reports.type_security")}</SelectItem>
          <SelectItem value="fire">{t("reports.type_fire")}</SelectItem>
          <SelectItem value="medical">{t("reports.type_medical")}</SelectItem>
          <SelectItem value="accident">{t("reports.type_accident")}</SelectItem>
          <SelectItem value="banditry">{t("reports.type_banditry")}</SelectItem>
          <SelectItem value="ecaution">{t("reports.type_ecaution")}</SelectItem>
          <SelectItem value="location_share">{t("reports.type_location_share")}</SelectItem>
        </SelectContent>
      </Select>
      <Select value={statusFilter} onValueChange={setStatusFilter}>
        <SelectTrigger className="w-36 rounded-sm border-gray-300 font-body text-sm" data-testid="status-filter-trigger">
          <SelectValue placeholder={t("reports.all_status")} />
        </SelectTrigger>
        <SelectContent className="rounded-sm">
          <SelectItem value="all">{t("reports.all_status")}</SelectItem>
          <SelectItem value="active">{t("reports.status_active")}</SelectItem>
          <SelectItem value="resolved">{t("reports.status_resolved")}</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

function ReportRow({ alert, isAdmin, onResolve, onDelete, onViewMap, isStriped }) {
  const Icon = typeIcons[alert.alert_type] || ShieldAlert;
  const iconColor = iconColorMap[alert.alert_type] || "#6B7280";

  return (
    <TableRow className={isStriped ? "bg-gray-50" : ""} data-testid={`report-row-${alert.alert_id}`}>
      <TableCell className="py-2 px-3">
        <div className="flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5" style={{ color: iconColor }} />
          <Badge className={`text-[10px] px-1.5 py-0 rounded-sm ${typeColors[alert.alert_type]}`}>
            {alert.alert_type}
          </Badge>
        </div>
      </TableCell>
      <TableCell className="py-2 px-3 text-sm text-gray-800 font-body max-w-[200px] truncate">
        {alert.description}
      </TableCell>
      <TableCell className="py-2 px-3 hidden md:table-cell">
        <div className="flex items-center gap-1 text-xs text-gray-500 font-body">
          <MapPin className="w-3 h-3" />
          {`${alert.latitude.toFixed(3)}, ${alert.longitude.toFixed(3)}`}
        </div>
      </TableCell>
      {isAdmin && (
        <TableCell className="py-2 px-3 hidden lg:table-cell text-xs text-gray-600 font-body">
          {alert.user_name}
        </TableCell>
      )}
      <TableCell className="py-2 px-3">
        <Badge className={`text-[10px] px-1.5 py-0 rounded-sm ${statusColors[alert.status]}`}>
          {alert.status}
        </Badge>
      </TableCell>
      <TableCell className="py-2 px-3 hidden sm:table-cell text-xs text-gray-500 font-body">
        {formatDate(alert.created_at)}
      </TableCell>
      <TableCell className="py-2 px-3">
        <div className="flex items-center gap-1">
          <Button
            variant="ghost" size="sm"
            onClick={() => onViewMap(alert)}
            className="h-6 text-[10px] text-blue-700 hover:text-blue-800 hover:bg-blue-50 rounded-sm px-2"
            data-testid={`view-map-${alert.alert_id}`}
          >
            <MapPin className="w-3 h-3 mr-1" /> Map
          </Button>
          {alert.status === "active" && (
            <Button
              variant="ghost" size="sm"
              onClick={() => onResolve(alert.alert_id)}
              className="h-6 text-[10px] text-green-700 hover:text-green-800 hover:bg-green-50 rounded-sm px-2"
              data-testid={`resolve-report-${alert.alert_id}`}
            >
              <CheckCircle className="w-3 h-3 mr-1" /> Resolve
            </Button>
          )}
          {isAdmin && (
            <Button
              variant="ghost" size="sm"
              onClick={() => onDelete(alert.alert_id)}
              className="h-6 text-[10px] text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-sm px-2"
              data-testid={`delete-report-${alert.alert_id}`}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

function TableEmptyState({ colSpan, message }) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} className="text-center py-12 text-sm text-gray-400 font-body">
        {message}
      </TableCell>
    </TableRow>
  );
}

export default function ReportsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);

  const fetchAlerts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (typeFilter !== "all") params.append("alert_type", typeFilter);
      if (statusFilter !== "all") params.append("status", statusFilter);
      const qs = params.toString();
      const url = qs ? `${API}/alerts?${qs}` : `${API}/alerts`;
      const res = await axios.get(url, { withCredentials: true });
      setAlerts(res.data);
    } catch (err) {
      logging.error("Failed to fetch alerts", err);
    }
    setLoading(false);
  }, [typeFilter, statusFilter]);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(res.data);
    } catch (err) {
      logging.error("Failed to fetch notifications", err);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
    fetchNotifications();
  }, [fetchAlerts, fetchNotifications]);

  const handleResolve = async (alertId) => {
    try {
      await axios.patch(`${API}/alerts/${alertId}/resolve`, {}, { withCredentials: true });
      fetchAlerts();
      toast.success(t("reports.resolved"));
    } catch {
      toast.error(t("reports.resolve_failed"));
    }
  };

  const handleViewMap = (alert) => {
    navigate(`/dashboard?lat=${alert.latitude}&lng=${alert.longitude}&alert_id=${alert.alert_id}`);
  };

  const handleDelete = async (alertId) => {
    if (!window.confirm(t("reports.delete_confirm"))) return;
    try {
      await axios.delete(`${API}/alerts/${alertId}`, { withCredentials: true });
      fetchAlerts();
      toast.success(t("reports.deleted"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("reports.delete_failed"));
    }
  };

  const handleClearResolved = async () => {
    if (!window.confirm(t("reports.clear_resolved_confirm"))) return;
    try {
      const r = await axios.delete(`${API}/alerts`, { params: { status_filter: "resolved" }, withCredentials: true });
      fetchAlerts();
      toast.success(t(r.data.deleted === 1 ? "reports.cleared_n_one" : "reports.cleared_n", { n: r.data.deleted }));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("reports.clear_failed"));
    }
  };

  const isAdmin = user?.role === "system_admin" || user?.role === "govt_admin";
  const colSpan = isAdmin ? 7 : 6;

  return (
    <div className="min-h-screen bg-[#F3F4F6]" data-testid="reports-page">
      <Header notifications={notifications} onNotificationRead={fetchNotifications} />

      <div className="p-4 md:p-6 max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tighter text-gray-900">{t("reports.title")}</h1>
            <p className="text-sm text-gray-500 font-body mt-0.5">
              {isAdmin ? t("reports.subtitle_admin") : t("reports.subtitle_user")}
            </p>
          </div>
          <ReportFilters
            typeFilter={typeFilter} setTypeFilter={setTypeFilter}
            statusFilter={statusFilter} setStatusFilter={setStatusFilter}
          />
        </div>

        {/* Export buttons */}
        <div className="flex items-center gap-2 mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { exportCSV(alerts); toast.success(t("reports.csv_downloaded")); }}
            className="rounded-sm font-body text-xs border-gray-300"
            data-testid="export-csv-btn"
            disabled={alerts.length === 0}
          >
            <Download className="w-3.5 h-3.5 mr-1.5" /> {t("reports.export_csv")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { exportPDF(alerts); toast.success(t("reports.pdf_downloaded")); }}
            className="rounded-sm font-body text-xs border-gray-300"
            data-testid="export-pdf-btn"
            disabled={alerts.length === 0}
          >
            <FileText className="w-3.5 h-3.5 mr-1.5" /> {t("reports.export_pdf")}
          </Button>
          <span className="text-[10px] text-gray-400 font-body ml-1">
            {t(alerts.length === 1 ? "reports.count_label_one" : "reports.count_label", { n: alerts.length })}
          </span>
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearResolved}
              className="rounded-sm font-body text-xs border-gray-300 hover:bg-red-50 hover:text-red-700 hover:border-red-300 ml-auto"
              data-testid="clear-resolved-btn"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> {t("reports.clear_resolved")}
            </Button>
          )}
        </div>

        <Tabs defaultValue="table" className="space-y-4">
          <TabsList className="bg-white border border-gray-200 rounded-sm p-0.5">
            <TabsTrigger value="table" className="rounded-sm text-sm font-body data-[state=active]:bg-black data-[state=active]:text-white" data-testid="tab-table">
              <TableIcon className="w-4 h-4 mr-1.5" /> {t("reports.tab_table")}
            </TabsTrigger>
            <TabsTrigger value="timeline" className="rounded-sm text-sm font-body data-[state=active]:bg-black data-[state=active]:text-white" data-testid="tab-timeline">
              <Clock className="w-4 h-4 mr-1.5" /> {t("reports.tab_timeline")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="table">
            <div className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid="reports-table">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-xs uppercase tracking-widest font-bold text-gray-500 py-2 px-3">{t("reports.col_type")}</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest font-bold text-gray-500 py-2 px-3">{t("reports.col_description")}</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest font-bold text-gray-500 py-2 px-3 hidden md:table-cell">{t("reports.col_location")}</TableHead>
                    {isAdmin && <TableHead className="text-xs uppercase tracking-widest font-bold text-gray-500 py-2 px-3 hidden lg:table-cell">{t("reports.col_reporter")}</TableHead>}
                    <TableHead className="text-xs uppercase tracking-widest font-bold text-gray-500 py-2 px-3">{t("reports.col_status")}</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest font-bold text-gray-500 py-2 px-3 hidden sm:table-cell">{t("reports.col_date")}</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest font-bold text-gray-500 py-2 px-3 w-20">{t("reports.col_action")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && <TableEmptyState colSpan={colSpan} message={t("common.loading")} />}
                  {!loading && alerts.length === 0 && <TableEmptyState colSpan={colSpan} message={t("reports.no_reports")} />}
                  {!loading && alerts.map((alert, idx) => (
                    <ReportRow
                      key={alert.alert_id}
                      alert={alert}
                      isAdmin={isAdmin}
                      isStriped={idx % 2 === 1}
                      onResolve={handleResolve}
                      onDelete={handleDelete}
                      onViewMap={handleViewMap}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="timeline">
            <AlertTimeline alerts={alerts} onResolve={handleResolve} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
